import React, { useState, useRef } from 'react';
import JSZip from 'jszip';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faFileArrowUp, faXmark, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

library.add(faFileArrowUp, faXmark, faChevronDown);

// Default accepted types — extend this array to support additional file types.
// Each entry describes how to handle a given extension.
// `extractable: true` means the file acts as an archive; its contents are extracted.
const DEFAULT_ACCEPTED_TYPES = [
  { ext: 'xml',  label: 'XML',  mime: ['text/xml', 'application/xml'] },
  { ext: 'xaf',  label: 'XAF',  mime: ['application/octet-stream', ''] },
  { ext: 'zip',  label: 'ZIP',  mime: ['application/zip', 'application/x-zip-compressed'], extractable: true },
];

const FileDropComponent = ({ onFilesAccepted, acceptedTypes = DEFAULT_ACCEPTED_TYPES, showFileList = true }) => {
  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [filesExpanded, setFilesExpanded] = useState(true);
  const inputRef = useRef(null);

  function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function getFileType(filename) {
    return filename.split('.').pop().toUpperCase();
  }

  const acceptedExts = acceptedTypes.map(t => t.ext.toLowerCase());
  const extractableExts = acceptedTypes.filter(t => t.extractable).map(t => t.ext.toLowerCase());

  function getExtension(filename) {
    return filename.split('.').pop().toLowerCase();
  }

  function isAccepted(filename) {
    return acceptedExts.includes(getExtension(filename));
  }

  async function extractZip(file) {
    const zip = await JSZip.loadAsync(file);
    const extracted = [];
    const promises = [];

    zip.forEach((relativePath, zipEntry) => {
      if (!zipEntry.dir && isAccepted(zipEntry.name)) {
        const promise = zipEntry.async('blob').then(blob => {
          const ext = getExtension(zipEntry.name);
          const mimeEntry = acceptedTypes.find(t => t.ext === ext);
          const mime = mimeEntry?.mime?.[0] || '';
          extracted.push(new File([blob], zipEntry.name, { type: mime }));
        });
        promises.push(promise);
      }
    });

    await Promise.all(promises);
    return extracted;
  }

  async function processFileList(fileList) {
    setError(null);
    const incoming = Array.from(fileList);
    const valid = incoming.filter(f => isAccepted(f.name));
    const invalid = incoming.filter(f => !isAccepted(f.name));

    if (invalid.length > 0) {
      setError(`Unsupported file type(s): ${invalid.map(f => f.name).join(', ')}`);
    }

    const resolved = [];

    for (const file of valid) {
      const ext = getExtension(file.name);
      if (extractableExts.includes(ext)) {
        const extracted = await extractZip(file);
        if (extracted.length === 0) {
          setError(`No supported files found inside ${file.name}`);
        }
        resolved.push(...extracted);
      } else {
        resolved.push(file);
      }
    }

    if (resolved.length === 0) return;

    const updated = [...files, ...resolved];
    setFiles(updated);
    onFilesAccepted?.(updated);
  }

  function handleDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setDragging(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    processFileList(e.dataTransfer.files);
  }

  function handleInputChange(e) {
    processFileList(e.target.files);
    e.target.value = '';
  }

  function removeFile(index) {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    onFilesAccepted?.(updated);
  }

  const acceptAttr = acceptedTypes
    .flatMap(t => [`.${t.ext}`, ...(t.mime || [])])
    .filter(Boolean)
    .join(',');

  const labelList = acceptedTypes.map(t => t.label).join(', ');

  return (
    <div className="file-drop-wrapper">
      <div
        className={`file-drop-zone${dragging ? ' file-drop-zone--active' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptAttr}
          className="file-drop-input"
          onChange={handleInputChange}
        />
        <FontAwesomeIcon icon={faFileArrowUp} className="file-drop-icon" />
        <p className="file-drop-label">
          Drag &amp; drop files here, or <span className="file-drop-browse">browse</span>
        </p>
        <p className="file-drop-hint">Supported: {labelList}</p>
      </div>

      {error && <p className="file-drop-error">{error}</p>}

      {showFileList && files.length > 0 && (
        <div className="file-drop-table-wrapper">
          <div className="file-drop-table-header" onClick={() => setFilesExpanded(v => !v)}>
            <span>{files.length} bestand{files.length !== 1 ? 'en' : ''} geselecteerd</span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`file-drop-table-chevron${filesExpanded ? ' rotated' : ''}`}
            />
          </div>
          {filesExpanded && (
            <table className="file-drop-table">
              <thead>
                <tr>
                  <th>Bestandsnaam</th>
                  <th>Grootte</th>
                  <th>Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {files.map((file, i) => (
                  <tr key={i}>
                    <td>{file.name}</td>
                    <td className="file-drop-item-size">{formatSize(file.size)}</td>
                    <td className="file-drop-item-type">{getFileType(file.name)}</td>
                    <td>
                      <button
                        className="file-drop-item-remove"
                        onClick={() => removeFile(i)}
                        type="button"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default FileDropComponent;
